import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Badge,
} from '@mui/material';
import {
  Send as SendIcon,
  MarkEmailRead as ReadIcon,
} from '@mui/icons-material';
import { portalApi, PortalNachricht, SendeNachrichtDto } from '../../services/portalService';

const PortalNachrichtenPage: React.FC = () => {
  const [nachrichten, setNachrichten] = useState<PortalNachricht[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [newMessage, setNewMessage] = useState<SendeNachrichtDto>({ betreff: '', inhalt: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadNachrichten();
  }, []);

  const loadNachrichten = async () => {
    try {
      setLoading(true);
      const response = await portalApi.getNachrichten();
      setNachrichten(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Nachrichten');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id: number) => {
    try {
      await portalApi.markiereGelesen(id);
      loadNachrichten();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await portalApi.markiereAlleGelesen();
      loadNachrichten();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleSend = async () => {
    if (!newMessage.betreff || !newMessage.inhalt) return;

    try {
      setSending(true);
      await portalApi.sendeNachricht(newMessage);
      setComposeOpen(false);
      setNewMessage({ betreff: '', inhalt: '' });
      loadNachrichten();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const unreadCount = nachrichten.filter((n) => !n.vomPortalBenutzer && !n.gelesen).length;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Nachrichten
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="primary" sx={{ ml: 2 }} />
          )}
        </Typography>
        <Box display="flex" gap={1}>
          {unreadCount > 0 && (
            <Button
              startIcon={<ReadIcon />}
              onClick={handleMarkAllRead}
            >
              Alle als gelesen markieren
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setComposeOpen(true)}
          >
            Neue Nachricht
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        {nachrichten.length === 0 ? (
          <Box p={3}>
            <Typography color="textSecondary" align="center">
              Keine Nachrichten vorhanden
            </Typography>
          </Box>
        ) : (
          <List>
            {nachrichten.map((msg, index) => (
              <React.Fragment key={msg.portalNachrichtID}>
                {index > 0 && <Divider />}
                <ListItemButton
                  onClick={() => !msg.gelesen && !msg.vomPortalBenutzer && handleMarkRead(msg.portalNachrichtID)}
                  sx={{
                    bgcolor: !msg.gelesen && !msg.vomPortalBenutzer ? 'action.hover' : 'inherit',
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="subtitle1"
                          fontWeight={!msg.gelesen && !msg.vomPortalBenutzer ? 'bold' : 'normal'}
                        >
                          {msg.betreff}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(msg.gesendetAm).toLocaleString('de-CH')}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                          {msg.vomPortalBenutzer ? 'Von Ihnen' : msg.absenderName || 'Mitarbeiter'}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {msg.inhalt.length > 200 ? msg.inhalt.substring(0, 200) + '...' : msg.inhalt}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Neue Nachricht</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Betreff"
              value={newMessage.betreff}
              onChange={(e) => setNewMessage({ ...newMessage, betreff: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Nachricht"
              value={newMessage.inhalt}
              onChange={(e) => setNewMessage({ ...newMessage, inhalt: e.target.value })}
              fullWidth
              required
              multiline
              rows={6}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={!newMessage.betreff || !newMessage.inhalt || sending}
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Senden
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalNachrichtenPage;
